/**
 * The stream: quick capture on top, then everything captured, newest first, in a virtualised
 * list (PLAN.md WP0.5 — the list must stay light at thousands of items). Archiving moves a
 * capture onto a page and offers undo in a toast, per the house rule.
 */
import { useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ApiError,
  api,
  type CaptureInfo,
  type Space,
  type Suggestion,
} from "../../api";
import { Dialog } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Button, Card, Field } from "../../components/primitives";
import { SkeletonText } from "../../components/Skeleton";
import { useToast } from "../../components/toast";
import { formatWhen } from "../../time";
import { CAPTURE_CREATED_EVENT, CaptureComposer } from "./CaptureComposer";

const PAGE_SIZE = 50;

export function StrommenPage() {
  const showToast = useToast();

  const [spaces, setSpaces] = useState<Space[] | null>(null);
  const [filterSpaceId, setFilterSpaceId] = useState<string>("");
  const [showArchived, setShowArchived] = useState(false);
  const [items, setItems] = useState<CaptureInfo[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [archiveTarget, setArchiveTarget] = useState<CaptureInfo | null>(null);

  useEffect(() => {
    void api.spaces().then(setSpaces);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const page = await api.captures({
        spaceId: filterSpaceId || undefined,
        includeArchived: showArchived,
        limit: PAGE_SIZE,
      });
      setItems(page.items);
      setNextCursor(page.next_cursor);
    } finally {
      setLoading(false);
    }
  }, [filterSpaceId, showArchived]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // A capture made anywhere (the global quick-capture dialog) appears here immediately.
  useEffect(() => {
    const onCreated = (event: Event) => {
      const capture = (event as CustomEvent<CaptureInfo>).detail;
      if (filterSpaceId && capture.space_id !== filterSpaceId) {
        return;
      }
      setItems((current) =>
        current.some((item) => item.id === capture.id) ? current : [capture, ...current],
      );
    };
    window.addEventListener(CAPTURE_CREATED_EVENT, onCreated);
    return () => window.removeEventListener(CAPTURE_CREATED_EVENT, onCreated);
  }, [filterSpaceId]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) {
      return;
    }
    const page = await api.captures({
      spaceId: filterSpaceId || undefined,
      includeArchived: showArchived,
      before: nextCursor,
      limit: PAGE_SIZE,
    });
    setItems((current) => [...current, ...page.items]);
    setNextCursor(page.next_cursor);
  }, [nextCursor, filterSpaceId, showArchived]);

  const removeItem = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const archive = useCallback(
    async (capture: CaptureInfo, pageId: string, pageTitle: string) => {
      setArchiveTarget(null);
      try {
        await api.archiveCapture(capture.id, pageId);
      } catch (error) {
        showToast({
          message: error instanceof ApiError ? error.message : "Arkiveringen misslyckades.",
          tone: "fail",
        });
        return;
      }
      if (!showArchived) {
        removeItem(capture.id);
      } else {
        void reload();
      }
      showToast({
        message: `Arkiverad till ”${pageTitle}”.`,
        undo: () => {
          void api
            .unarchiveCapture(capture.id)
            .then(() => void reload())
            .catch(() => showToast({ message: "Kunde inte ångra.", tone: "fail" }));
        },
      });
    },
    [showArchived, removeItem, reload, showToast],
  );

  if (spaces === null) {
    return <SkeletonText lines={4} />;
  }

  return (
    <div className="grid content-start gap-s6">
      <Card hero>
        <CaptureComposer spaces={spaces} cta autoFocus />
      </Card>

      <div className="flex flex-wrap items-center gap-s5">
        <label className="flex items-center gap-s3">
          <span className="nb-overline border-0 pb-0">Utrymme</span>
          <select
            className="nb-field"
            value={filterSpaceId}
            onChange={(event) => setFilterSpaceId(event.target.value)}
          >
            <option value="">Alla utrymmen</option>
            {spaces.map((space) => (
              <option key={space.id} value={space.id}>
                {space.kind === "private" ? `${space.name} (privat)` : space.name}
              </option>
            ))}
          </select>
        </label>
        <label className="ml-auto flex items-center gap-s3 text-body text-ink">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(event) => setShowArchived(event.target.checked)}
            style={{ accentColor: "var(--drop)" }}
          />
          Visa arkiverade
        </label>
      </div>

      {loading ? (
        <SkeletonText lines={6} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Strömmen är tom"
          description="Fånga en tanke i fältet ovanför, eller tryck C var du än är i appen. Allt du fångar hamnar här, nyast överst."
        />
      ) : (
        <StreamList
          items={items}
          hasMore={nextCursor !== null}
          onLoadMore={() => void loadMore()}
          onArchive={setArchiveTarget}
        />
      )}

      <ArchiveDialog
        capture={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onPick={(pageId, pageTitle) =>
          archiveTarget && void archive(archiveTarget, pageId, pageTitle)
        }
      />
    </div>
  );
}

// --- the virtualised list --------------------------------------------------------------------------

function StreamList({
  items,
  hasMore,
  onLoadMore,
  onArchive,
}: {
  items: CaptureInfo[];
  hasMore: boolean;
  onLoadMore: () => void;
  onArchive: (capture: CaptureInfo) => void;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(480);

  // Fill the viewport from wherever the list starts — the list is the page's scroll surface.
  useEffect(() => {
    const measure = () => {
      const top = parentRef.current?.getBoundingClientRect().top ?? 0;
      setHeight(Math.max(320, window.innerHeight - top - 24));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const rowCount = items.length + (hasMore ? 1 : 0);
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120,
    overscan: 8,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // When the sentinel row after the last capture scrolls into view, fetch the next slice.
  const lastVisible = virtualItems.at(-1);
  useEffect(() => {
    if (hasMore && lastVisible && lastVisible.index >= items.length) {
      onLoadMore();
    }
  }, [hasMore, lastVisible, items.length, onLoadMore]);

  return (
    <div ref={parentRef} className="overflow-y-auto" style={{ height }}>
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualItems.map((row) => {
          const capture = items[row.index];
          return (
            <div
              key={row.key}
              data-index={row.index}
              ref={virtualizer.measureElement}
              className="absolute left-0 top-0 w-full pb-s4"
              style={{ transform: `translateY(${row.start}px)` }}
            >
              {capture ? (
                <CaptureCard capture={capture} onArchive={onArchive} />
              ) : (
                <SkeletonText lines={2} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CaptureCard({
  capture,
  onArchive,
}: {
  capture: CaptureInfo;
  onArchive: (capture: CaptureInfo) => void;
}) {
  const navigate = useNavigate();
  const archived = capture.archived_at !== null;

  return (
    <Card className={archived ? "opacity-70" : undefined}>
      <div className="flex items-baseline gap-s4">
        <span className="text-small font-medium text-ink">{capture.author ?? "Okänd"}</span>
        <time dateTime={capture.created_at} className="nb-num text-small text-muted">
          {formatWhen(capture.created_at)}
        </time>
        {archived ? (
          capture.archived_to_page_id ? (
            <button
              type="button"
              className="nb-link ml-auto border-0 bg-transparent p-0 text-small"
              onClick={() => navigate(`/sidor/${capture.archived_to_page_id}`)}
            >
              Arkiverad → {capture.archived_to_page_title ?? "sida"}
            </button>
          ) : (
            <span className="ml-auto text-small text-muted">Arkiverad</span>
          )
        ) : (
          <Button variant="ghost" className="ml-auto" onClick={() => onArchive(capture)}>
            Arkivera …
          </Button>
        )}
      </div>

      {capture.body && (
        <p className="mt-s3 whitespace-pre-wrap text-body text-ink">{capture.body}</p>
      )}

      {capture.attachments.length > 0 && (
        <ul className="mt-s4 flex flex-wrap gap-s4">
          {capture.attachments.map((attachment) => (
            <li key={attachment.id}>
              {attachment.mime_type.startsWith("image/") ? (
                <a href={attachment.url} target="_blank" rel="noreferrer">
                  <img
                    src={attachment.thumbnail_url ?? attachment.url}
                    alt={attachment.filename}
                    loading="lazy"
                    className="max-h-[160px] rounded-card border border-line"
                  />
                </a>
              ) : (
                <a className="nb-link text-body" href={attachment.url} target="_blank" rel="noreferrer">
                  {attachment.filename}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// --- archiving -------------------------------------------------------------------------------------

function ArchiveDialog({
  capture,
  onClose,
  onPick,
}: {
  capture: CaptureInfo | null;
  onClose: () => void;
  onPick: (pageId: string, pageTitle: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  useEffect(() => {
    if (!capture) {
      setQuery("");
      setSuggestions([]);
      return;
    }
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void api
        .suggest(query.trim(), capture.space_id)
        .then((found) => setSuggestions(found.filter((s) => s.kind === "page")));
    }, 150);
    return () => window.clearTimeout(timer);
  }, [capture, query]);

  return (
    <Dialog
      open={capture !== null}
      title="Arkivera till sida"
      subtitle="Innehållet kopieras till sidan"
      onClose={onClose}
    >
      <div className="grid gap-s4">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const first = suggestions[0];
            if (first) {
              onPick(first.id, first.title);
            }
          }}
        >
          <Field
            label="Sök sida eller projekt"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            help="Fångsten läggs till i slutet av sidan och lämnar strömmen."
            autoFocus
          />
        </form>
        {suggestions.length > 0 && (
          <ul className="grid gap-s2">
            {suggestions.map((suggestion) => (
              <li key={suggestion.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-s4 rounded-control px-s4 py-s3 text-left text-body text-ink transition-[background] duration-control ease-nb hover:bg-surface-2"
                  onClick={() => onPick(suggestion.id, suggestion.title)}
                >
                  <span>{suggestion.title}</span>
                  {suggestion.hint && (
                    <span className="text-small text-muted">{suggestion.hint}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {query.trim() && suggestions.length === 0 && (
          <p className="text-small text-muted">Inga sidor matchar i fångstens utrymme.</p>
        )}
      </div>
    </Dialog>
  );
}

